import { Link, useLocation, useNavigate } from "react-router-dom";
import { workspacePaths, workspaceTab } from "./catalogRoutes";
import React, { useCallback, useEffect, useRef, useState } from "react";
import OrderAnalyticsPage from "./OrderAnalyticsPage";
import ProfileControl from "../component/ProfileControl";
import CatalogPrototypePage from "./CatalogPrototypePage";
import DocumentProcessor from "../component/DocumentProcessor/DocumentProcessor";
import NewAnalysisPage from "./NewAnalysisPage";
import { ChipDipCollectionProvider } from "../component/ChipDipCollection";
import { getSession } from "../api/auth";
import useDocumentPage from "./useDocumentPage";

import { Cpu, Search, FileText, ChartNoAxesColumnIncreasing, CircleHelp, ChevronDown, Settings, Users, ChartPie } from "lucide-react";
import styles from "./CatalogWorkspace.module.css";

// [[Catalog Prototype]] #obsidian-marker
export default function CatalogWorkspace() {
    const session = getSession();
    const initials = (session?.employeeName || "Пользователь").trim().split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]).join("").toUpperCase();
    const location = useLocation();
    const navigate = useNavigate();
    const tab = workspaceTab(location);
    const catalogAddress = useRef("/catalog");
    const catalogSearch = useRef("");
    if (tab === "catalog") {
        catalogAddress.current = (location.pathname === "/catalog/categories" ? "/catalog/categories" : "/catalog") + location.search;
        catalogSearch.current = location.search;
    }
    useEffect(() => {
        if (location.pathname === "/" || location.pathname === "/sym") {
            const params = new URLSearchParams(location.search); params.delete("tab");
            navigate(workspacePaths[tab] + (params.size ? `?${params}` : ""), { replace: true });
        }
    }, [location.pathname, location.search, navigate, tab]);
    const [visited, setVisited] = useState(() => ({ [tab]: true }));
    const [documentType, setDocumentType] = useState("request");


    const [showMyOrders, setShowMyOrders] = useState(false);
    const [showMyOffers, setShowMyOffers] = useState(false);
    const orderPage = useDocumentPage('orders', showMyOrders, tab === 'analysis');
    const offerPage = useDocumentPage('offers', showMyOffers, tab === 'analysis');
    const setOrders = orderPage.refresh, setOffers = offerPage.refresh;
    const refresh = useCallback(() => { setOrders(); setOffers(); }, [setOrders, setOffers]);
    useEffect(() => { setVisited(current => current[tab] ? current : { ...current, [tab]: true }); }, [tab]);
    return <div>
        <header className={styles.header}>
            <div className={styles.brand}>
                <Cpu size={44} strokeWidth={1.8} aria-hidden="true" />
                <div><strong>Компоненты</strong><p>Поиск · BOM · Анализ цен</p></div>
            </div>
            <nav className={styles.navigation} aria-label="Разделы каталога">
                {[
                    ["analysis", "Анализ цен", "Сравнение предложений", ChartNoAxesColumnIncreasing],
                    ["catalog", "Каталог", "Поиск компонентов", Search],
                    ["bom", "BOM-анализ", "Загрузка и обработка", FileText],
                    ["analytics", "Аналитика", "Заказы и сотрудники", ChartPie]
                ].map(([key, label, description, Icon]) =>
                    <Link key={key} to={key === "catalog" ? catalogAddress.current : workspacePaths[key]} className={tab === key ? styles.selected : ""}
                        aria-label={label} aria-current={tab === key ? "page" : undefined} aria-controls={`catalog-panel-${key}`}>
                        <Icon size={30} strokeWidth={1.8} aria-hidden="true" />
                        <span><strong>{label}</strong><small>{description}</small></span>
                    </Link>)}
            </nav>
            <div className={styles.actions}>
                {session?.roles?.includes("ADMIN") && <a className={styles.adminButton} href="/sym/admin" aria-label="Администрирование" title="Администрирование"><Settings size={24} aria-hidden="true" /></a>}
                <details className={styles.dropdown}>
                    <summary aria-label="Помощь" title="Помощь"><CircleHelp size={24} aria-hidden="true" /></summary>
                    <div className={styles.popover}><strong>Работа с компонентами</strong><p>Каталог — поиск компонентов и предложений поставщиков.</p><p>BOM-анализ — загрузка и обработка запроса или предложения.</p><p>Анализ цен — сравнение предложений по загруженным документам.</p></div>
                </details>
                <ProfileControl session={session} className={styles.profileButton}>
                    {avatar => <>{avatar}<span className={styles.profileInitials}>{initials}</span><ChevronDown size={18} aria-hidden="true" /></>}
                </ProfileControl>
            </div>
        </header>
        {visited.analytics && <section id="catalog-panel-analytics" hidden={tab !== "analytics"}><OrderAnalyticsPage /></section>}
        <section id="catalog-panel-catalog" hidden={tab !== "catalog"}><CatalogPrototypePage catalogSearch={catalogSearch.current}
            catalogView={catalogAddress.current.split("?")[0] === "/catalog/categories" ? "categories" : "filters"}
            onViewChange={view => navigate((view === "categories" ? "/catalog/categories" : "/catalog") + catalogSearch.current)}
            onClearSelection={() => {
                const params = new URLSearchParams(catalogSearch.current);
                ["part", "manufacturer", "productId", "tab"].forEach(key => params.delete(key));
                navigate(catalogAddress.current.split("?")[0] + (params.size ? `?${params}` : ""), { replace: true });
            }} /></section>
        {visited.bom && <section id="catalog-panel-bom" hidden={tab !== "bom"} className={styles.panel}>
            <h2>Загрузить BOM</h2>
            <p className={styles.bomSubtitle}>Загрузите спецификацию для поиска компонентов и получения предложений</p>
            <nav className={styles.toolbar} aria-label="Тип BOM">
                <button type="button" aria-pressed={documentType === "request"} className={documentType === "request" ? styles.active : ""} onClick={() => setDocumentType("request")}><FileText size={24} aria-hidden="true" />Запрос</button>
                <button type="button" aria-pressed={documentType === "offer"} className={documentType === "offer" ? styles.active : ""} onClick={() => setDocumentType("offer")}><Users size={24} aria-hidden="true" />Предложение</button>
            </nav>
            {["request", "offer"].map(type => <div key={type} hidden={documentType !== type}>
                <DocumentProcessor catalogAppearance lockedDocumentType={type} setOrders={setOrders} setOffers={setOffers} />
            </div>)}
        </section>}
        {visited.analysis && <section id="catalog-panel-analysis" hidden={tab !== "analysis"}>
            <ChipDipCollectionProvider onCompleted={refresh}>
                <NewAnalysisPage catalogLinks session={getSession()} orders={orderPage.content} offers={offerPage.content} orderPage={orderPage} offerPage={offerPage}
                    setOrders={setOrders} setOffers={setOffers} showMyOrders={showMyOrders} showMyOffers={showMyOffers}
                    onToggleMyOrders={() => setShowMyOrders(current => !current)}
                    onToggleMyOffers={() => setShowMyOffers(current => !current)} />
            </ChipDipCollectionProvider>
        </section>}
    </div>;
}
